import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2 } from "lucide-react";
import * as api from "../../lib/api";

/** Create a new goal. If `studentId` is provided the student is fixed; otherwise
 * a student picker is shown (used from the all-students Goals page). */
export function AddGoalDialog({ open, onOpenChange, studentId, students = [], goalTypes = [], periods = [], templates = [], onTemplatesChanged, onCreated }) {
  const blank = { student_id: studentId || "", goal_type: "", goal_statement: "", reporting_period_id: "", goal_recommendation: "" };
  const [form, setForm] = useState(blank);
  const [objectives, setObjectives] = useState([{ description: "", measurement: "", progress_status: "" }]);
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState("");

  const reset = () => { setForm({ ...blank, student_id: studentId || "" }); setObjectives([{ description: "", measurement: "", progress_status: "" }]); setTemplateId(""); };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((p) => ({ ...p, goal_type: t.goal_type || p.goal_type, goal_statement: t.goal_statement || p.goal_statement }));
    const objs = (t.objectives || []).map((o) => ({ description: o.description || "", measurement: o.measurement || "", progress_status: o.progress_status || "" }));
    setObjectives(objs.length ? objs : [{ description: "", measurement: "", progress_status: "" }]);
    toast.success(`Template "${t.name}" applied`);
  };

  const deleteTemplate = async (id) => {
    try {
      await api.deleteGoalTemplate(id);
      if (templateId === id) setTemplateId("");
      toast.success("Template deleted");
      onTemplatesChanged && onTemplatesChanged();
    } catch (e) { toast.error("Failed to delete template"); }
  };

  const submit = async () => {
    const sid = studentId || form.student_id;
    if (!sid) { toast.error("Select a student"); return; }
    if (!form.goal_type.trim()) { toast.error("Choose a goal type"); return; }
    setSaving(true);
    try {
      await api.createGoal({
        student_id: sid,
        goal_type: form.goal_type.trim(),
        goal_statement: form.goal_statement,
        reporting_period_id: form.reporting_period_id || null,
        goal_recommendation: form.goal_recommendation,
        objectives: objectives.filter((o) => o.description.trim()),
      });
      toast.success("Goal created");
      reset();
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error("Failed to create goal");
    } finally { setSaving(false); }
  };

  const setObj = (i, k, v) => setObjectives((prev) => prev.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-goal-dialog">
        <DialogHeader>
          <DialogTitle>New Goal</DialogTitle>
          <DialogDescription>Link a goal to a student, set the type, statement, objectives and reporting period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <Label>Start from template <span className="text-xs text-slate-400">(optional)</span></Label>
              <div className="flex gap-2">
                <Select value={templateId} onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="goal-template-select"><SelectValue placeholder="Prefill from a saved template" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.goal_type ? ` · ${t.goal_type}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {templateId && (
                  <Button type="button" size="icon" variant="ghost" className="text-red-500 shrink-0" onClick={() => deleteTemplate(templateId)} title="Delete this template" data-testid="delete-template-btn"><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            </div>
          )}
          {!studentId && (
            <div>
              <Label>Student</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm((p) => ({ ...p, student_id: v }))}>
                <SelectTrigger data-testid="goal-student-select"><SelectValue placeholder="Select a student" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Goal Type</Label>
              <Input list="goal-type-options" value={form.goal_type} onChange={(e) => setForm((p) => ({ ...p, goal_type: e.target.value }))} placeholder="Reading, Writing, Behavior…" data-testid="goal-type-input" />
              <datalist id="goal-type-options">
                {goalTypes.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div>
              <Label>Reporting Period</Label>
              <Select value={form.reporting_period_id || "none"} onValueChange={(v) => setForm((p) => ({ ...p, reporting_period_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="goal-period-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reporting period</SelectItem>
                  {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Overall Goal Statement</Label>
            <Textarea value={form.goal_statement} onChange={(e) => setForm((p) => ({ ...p, goal_statement: e.target.value }))} placeholder="e.g. Jeremy will read a passage for 12-15 minutes at his independent reading level…" data-testid="goal-statement-input" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Objectives</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setObjectives((p) => [...p, { description: "", measurement: "", progress_status: "" }])}><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-2 mt-1">
              {objectives.map((o, i) => (
                <div key={i} className="rounded-md border border-slate-200 p-2 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-semibold text-slate-500 pt-2">{i + 1}:</span>
                    <Textarea value={o.description} onChange={(e) => setObj(i, "description", e.target.value)} placeholder="Stepped benchmark description" className="min-h-[48px]" data-testid={`new-goal-obj-desc-${i}`} />
                    {objectives.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-500 mt-1" onClick={() => setObjectives((p) => p.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 pl-6">
                    <Input value={o.progress_status} onChange={(e) => setObj(i, "progress_status", e.target.value)} placeholder="Current progress" className="w-48" />
                    <Input value={o.measurement} onChange={(e) => setObj(i, "measurement", e.target.value)} placeholder="Measurement" className="w-48" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Goal Recommendation <span className="text-xs text-slate-400">(optional, added at reporting time)</span></Label>
            <Textarea value={form.goal_recommendation} onChange={(e) => setForm((p) => ({ ...p, goal_recommendation: e.target.value }))} placeholder="e.g. Preserve goal / Reduce difficulty level…" data-testid="goal-recommendation-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-new-goal">{saving ? "Saving…" : "Create Goal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
